import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Tab } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import ShareIcon from "@mui/icons-material/Share";
import KeyIcon from "../assets/iconComponents/KeyIcon";
import { useProject } from "../context/ProjectContext";
// import { useNotification } from '../context/NotificationContext'
import ProjectService from "../services/ProjectService";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import BreadcrumbsNav from "../components/BreadcrumbsNav";
import GeneralTab from "../components/projectSettings/general/GeneralTab";
import LoginTab from "../components/projectSettings/login/LoginTab";
import TestCaseConfigTab from "../components/projectSettings/testCaseConfig/TestCaseConfigTab";
import ShareProjectTab from "../components/projectSettings/share/ShareProjectTab";
import UpdateLoginCredentialsModal from "../components/modals/UpdateLoginCredentialsModal";
import { validateProjectName } from "../utils/validateForm";

const ProjectSettings = () => {
    const { selectedProject, setSelectedProject } = useProject();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: routeProjectId } = useParams();
    // const showNotification = useNotification()
    const projectId = routeProjectId || selectedProject?.id;

    const [activeTabValue, setActiveTabValue] = useState("1");
    const [historyPageId, setHistoryPageId] = useState(null);
    const [project, setProject] = useState(null);
    const [projectName, setProjectName] = useState("");
    const [projectNameError, setProjectNameError] = useState("");
    const [allIssueTypes, setAllIssueTypes] = useState([]);
    const [projectIssueTypes, setProjectIssueTypes] = useState([
        "Server response error (e.g., 401, 403)",
    ]);
    const [projectIssueTypesError, setProjectIssueTypesError] = useState(false);
    const [saveProjectLoading, setSaveProjectLoading] = useState(false);
    const [environment, setEnvironment] = useState("Staging");
    const [resolutions, setResolutions] = useState({
        desktop: false,
        tablet: false,
        mobile: false,
    });
    const [loginRequired, setLoginRequired] = useState(false);
    const [isLoginDataExist, setIsLoginDataExist] = useState(false);
    const [openLoginRequiredModal, setOpenLoginRequiredModal] = useState(false);

    const [url, setUrl] = useState("");
    const [urlError, setUrlError] = useState(false);
    const [loginFormId, setLoginFormId] = useState("");
    const [login, setLogin] = useState("");
    const [loginError, setLoginError] = useState(false);
    const [isPasswordExist, setIsPasswordExist] = useState(null);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [elementsIdRoles, setElementsIdRoles] = useState([]);
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedElement, setSelectedElement] = useState(null);
    // const [rescanLoginPageLoading, setRescanLoginPageLoading] = useState(false)
    const canShareProject = Boolean(project?.is_owner);

    useEffect(() => {
        const getProjectData = async () => {
            try {
                const allIssueTypesData = await ProjectService.getIssueTypes();
                setAllIssueTypes(allIssueTypesData);
                const projectData = await ProjectService.getProjectById(
                    projectId
                );
                setSelectedProject(projectData);
                setLoginRequired(projectData?.login_required);
                if (!projectData?.login_required) {
                    setActiveTabValue((prevTab) =>
                        prevTab === "2" ? "1" : prevTab
                    );
                }
                if (!projectData?.is_owner) {
                    setActiveTabValue((prevTab) =>
                        prevTab === "4" ? "1" : prevTab
                    );
                }
                setIsLoginDataExist(projectData?.is_auth_data);
                setProject(projectData);
                setProjectName(projectData.name);
                setEnvironment(projectData.environment);
                setResolutions({
                    desktop: projectData.screen_sizes.includes("desktop"),
                    tablet: projectData.screen_sizes.includes("tablet"),
                    mobile: projectData.screen_sizes.includes("mobile"),
                });
                const projectIssueTypesData = allIssueTypesData
                    .filter((type) =>
                        projectData.issue_class_ids.includes(type.id)
                    )
                    .map((type) => type.description);
                setProjectIssueTypes(projectIssueTypesData);
            } catch (error) {
                console.log(error);
            }
        };

        if (projectId) {
            getProjectData();
        }
    }, [projectId, setSelectedProject]);

    useEffect(() => {
        if (location.state?.historyPageId) {
            setHistoryPageId(location.state.historyPageId);
        }
        if (location.state?.activeTab) {
            setActiveTabValue(location.state?.activeTab);
        }
    }, [location.state]);

    useEffect(() => {
        if (project && !project.is_owner && activeTabValue === "4") {
            setActiveTabValue("1");
        }
    }, [activeTabValue, project]);

    const handleProjectNameChange = (e) => {
        setProjectName(e.target.value);
        if (projectNameError) {
            setProjectNameError("");
        }
    };

    const handleEnvironmentChange = (newValue) => {
        setEnvironment(newValue);
    };

    const handleResolutionsChange = (event) => {
        setResolutions({
            ...resolutions,
            [event.target.name]: event.target.checked,
        });
    };

    const handleProjectIssueTypesChange = (event) => {
        const selectedTypes = event.target.value;

        if (!selectedTypes.includes("Server response error (e.g., 401, 403)")) {
            setProjectIssueTypes([
                "Server response error (e.g., 401, 403)",
                ...selectedTypes,
            ]);
        } else {
            setProjectIssueTypes(selectedTypes);
        }

        setProjectIssueTypesError(selectedTypes?.length === 0);
    };

    const handleChangeActiveTab = (event, newValue) => {
        setActiveTabValue(newValue);
    };

    const handleUrlChange = (value) => {
        setUrl(value);
        setUrlError(false);
    };

    const handleLoginChange = (value) => {
        setLogin(value);
        setLoginError(false);
    };

    const handlePasswordChange = (value) => {
        setPassword(value);
        setPasswordError(false);
    };

    const handleEditPassword = () => {
        setIsEditingPassword(true);
    };

    const handleNewPasswordChange = (e) => {
        setNewPassword(e.target.value);
    };

    const handleSavePassword = () => {
        setIsEditingPassword(false);
    };

    const handleClosePassword = () => {
        setIsEditingPassword(false);
        setNewPassword("");
    };

    const handleLoginRequiredChange = (e) => {
        const isChecked = e.target.checked;
        if (isChecked && !isLoginDataExist) {
            setLoginRequired(true);
            setOpenLoginRequiredModal(true);
        }
        if (isChecked && isLoginDataExist) {
            setLoginRequired(true);
        }
        if (!isChecked) {
            setLoginRequired(false);
        }
    };

    const handleCloseLoginRequiredData = () => {
        setOpenLoginRequiredModal(false);
        // If user cancels, revert the login required state
        if (!isLoginDataExist) {
            setLoginRequired(false);
        }
    };

    const handleSaveLoginRequiredData = () => {
        // When credentials are saved, switch to login tab and refresh data
        setActiveTabValue("2");
        setOpenLoginRequiredModal(false);
        setLoginRequired(true);
        // Refresh the page data to get updated login information
        window.location.reload();
    };

    // const prepareUpdatedProjectLoginData = () => {
    //   const LoginData = {
    //     loginPageUrl: url,
    //     login,
    //   }

    //   if (loginFormId) {
    //     LoginData.loginFormId = loginFormId
    //   }

    //   if (isPasswordExist && newPassword) {
    //     LoginData.password = newPassword
    //   } else if (password) {
    //     LoginData.password = password
    //   }

    //   if (elementsIdRoles.length) {
    //     LoginData.elementsIdRoles = elementsIdRoles.filter(element => element.role)
    //   }

    //   return LoginData
    // }

    // const validateElementsIdRoles = () => {
    //   const requiredRoles = ["Login input", "Password input", "Sign-in button"]
    //   return requiredRoles.every(role =>
    //     elementsIdRoles.some(item => item.role === role)
    //   )
    // }

    // const handleRescanLoginPage = async () => {
    //   setRescanLoginPageLoading(true)
    //   setElementsIdRoles([])
    //   try {
    //     await ProjectService.rescanProjectLoginData(selectedProject.id, { loginPageUrl: url, loginFormId })
    //   } catch (error) {
    //     console.log(error)
    //   }
    // }

    const handleCancel = () => {
        if (historyPageId) {
            navigate(`/pages/${historyPageId}`);
        } else {
            navigate(`/`);
        }
    };

    const handleGeneralSettingsSave = async () => {
        setSaveProjectLoading(true);

        if (!validateProjectName(projectName)) {
            setProjectNameError("Invalid name.");
            setSaveProjectLoading(false);
            return;
        }

        const issueClassIds = allIssueTypes
            .filter((type) => projectIssueTypes.includes(type.description))
            .map((type) => type.id);
        const updatedScreenSizes = Object.keys(resolutions).filter(
            (res) => resolutions[res]
        );

        try {
            const updatedProjectData = await ProjectService.updateProject(
                projectId,
                projectName,
                environment,
                issueClassIds,
                updatedScreenSizes,
                loginRequired
            );
            setProject(updatedProjectData);

            if (historyPageId) {
                navigate(`/pages/${historyPageId}`);
            } else {
                navigate(`/`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSaveProjectLoading(false);
        }
    };

    // const handleLoginSettingsSave = async () => {
    //   if (validateElementsIdRoles()) {
    //     setSaveProjectLoading(true)

    //     let isValid = true

    //     if (!url) {
    //       setUrlError(true)
    //       isValid = false
    //     }
    //     if (!login) {
    //       setLoginError(true)
    //       isValid = false
    //     }
    //     if (!isPasswordExist && !password) {
    //       setPasswordError(true)
    //       isValid = false
    //     }

    //     if (!isValid) {
    //       setSaveProjectLoading(false)
    //       return
    //     }

    //     try {
    //       const updatedProjectLoginData = prepareUpdatedProjectLoginData()
    //       await ProjectService.updateProjectLoginData(selectedProject.id, updatedProjectLoginData)

    //       if (historyPageId) {
    //         navigate(`/pages/${historyPageId}`)
    //       } else {
    //         navigate(`/`)
    //       }
    //     } catch (error) {
    //       console.log(error)
    //     } finally {
    //       setSaveProjectLoading(false)
    //     }
    //   } else {
    //     showNotification("You should choose 'Login Input', 'Password Input' and 'Sign-In Button' elements of you login page", "error", "", null)
    //   }
    // }

    return (
        <Box className="rootWrapper">
            <Helmet>
                <title>{`Treegress - ${project?.name} Project Settings`}</title>
            </Helmet>
            <Header />
            <Box className="srollContent">
                <Sidebar />
                {project && (
                    <Box className="projectSettings">
                        <BreadcrumbsNav pageLabel="settings" />
                        <Box className="projectSettingsTopSection">
                            <Typography className="projectSettingsTitle">
                                Project Settings
                            </Typography>
                            <Box className="projectSettingsActions">
                                {/* { loginRequired && (activeTabValue === "2" || activeTabValue === "3") ?
                  rescanLoginPageLoading ?
                    <Box className='projectSettingsRescanButtonContainer'>
                      <LoadingButton
                        className='projectSettingsRescanButton'
                        loading
                        variant="contained"
                        color="secondary"
                        loadingIndicator={<CircularProgress color="white" size={16} />}
                      >
                        RESCAN LOGIN PAGE
                      </LoadingButton>
                      <Divider orientation="vertical" flexItem />
                    </Box> :
                    <Box className='projectSettingsRescanButtonContainer'>
                      <Button
                        className='projectSettingsRescanButton'
                        onClick={handleRescanLoginPage}
                        variant="contained"
                        color="secondary"
                      >
                        RESCAN LOGIN PAGE
                      </Button>
                      <Divider orientation="vertical" flexItem />
                    </Box> : null
                } */}
                                {activeTabValue === "1" ? (
                                    <Button
                                        className="projectSettingsCancelButton"
                                        onClick={handleCancel}
                                        variant="outlined"
                                        color="secondary"
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                                {
                                    saveProjectLoading ? (
                                        <LoadingButton
                                            className="projectSettingsSaveButton"
                                            loading
                                            variant="contained"
                                            color="secondary"
                                            loadingIndicator={
                                                <CircularProgress
                                                    color="white"
                                                    size={16}
                                                />
                                            }
                                        >
                                            Save
                                        </LoadingButton>
                                    ) : activeTabValue === "1" ? (
                                        <Button
                                            className="projectSettingsSaveButton"
                                            onClick={handleGeneralSettingsSave}
                                            variant="contained"
                                            color="secondary"
                                        >
                                            Save
                                        </Button>
                                    ) : null
                                    // elementsIdRoles?.length ?
                                    //   <Button className='projectSettingsSaveButton' onClick={handleLoginSettingsSave} variant="contained" color="secondary">
                                    //     Save
                                    //   </Button> :
                                    //   <Button className='projectSettingsSaveButton' disabled variant="contained" color="secondary">
                                    //     Save
                                    //   </Button>
                                }
                            </Box>
                        </Box>
                        <TabContext value={activeTabValue}>
                            <TabList
                                className="projectSettingsTabsToggler"
                                onChange={handleChangeActiveTab}
                            >
                                <Tab
                                    className="projectSettingsTabLabelContainer"
                                    label={
                                        <Box
                                            className="projectSettingsTabLabel"
                                            sx={{
                                                color:
                                                    activeTabValue === "1"
                                                        ? "#4574D2"
                                                        : "rgba(0, 0, 0, 0.6)",
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                className="projectSettingsTabLabelIcon"
                                                color={
                                                    activeTabValue === "1"
                                                        ? "#4574D2"
                                                        : "rgba(0, 0, 0, 0.6)"
                                                }
                                            />
                                            <span className="projectSettingsTabLabelText">
                                                GENERAL
                                            </span>
                                        </Box>
                                    }
                                    value="1"
                                />
                                <Tab
                                    className="projectSettingsTabLabelContainer"
                                    label={
                                        <Box
                                            className="projectSettingsTabLabel"
                                            sx={{
                                                color:
                                                    activeTabValue === "2"
                                                        ? "#4574D2"
                                                        : "rgba(0, 0, 0, 0.6)",
                                            }}
                                        >
                                            <KeyIcon
                                                className="projectSettingsTabLabelIcon"
                                                color={
                                                    activeTabValue === "2"
                                                        ? "#4574D2"
                                                        : "rgba(0, 0, 0, 0.6)"
                                                }
                                            />
                                            <span className="projectSettingsTabLabelText">
                                                LOGIN
                                            </span>
                                        </Box>
                                    }
                                    value="2"
                                    disabled={!loginRequired}
                                />
                                <Tab
                                    className="projectSettingsTabLabelContainer"
                                    label={
                                        <Box
                                            className="projectSettingsTabLabel"
                                            sx={{
                                                color:
                                                    activeTabValue === "3"
                                                        ? "#4574D2"
                                                        : "rgba(0, 0, 0, 0.6)",
                                            }}
                                        >
                                            <SettingsSuggestOutlinedIcon
                                                className="projectSettingsTabLabelIcon"
                                            />
                                            <span className="projectSettingsTabLabelText">
                                                TEST CASE CONFIG
                                            </span>
                                        </Box>
                                    }
                                    value="3"
                                />
                                {canShareProject ? (
                                    <Tab
                                        className="projectSettingsTabLabelContainer"
                                        label={
                                            <Box
                                                className="projectSettingsTabLabel"
                                                sx={{
                                                    color:
                                                        activeTabValue === "4"
                                                            ? "#4574D2"
                                                            : "rgba(0, 0, 0, 0.6)",
                                                }}
                                            >
                                                <ShareIcon className="projectSettingsTabLabelIcon" />
                                                <span className="projectSettingsTabLabelText">
                                                    SHARE PROJECT
                                                </span>
                                            </Box>
                                        }
                                        value="4"
                                    />
                                ) : null}
                            </TabList>
                            <TabPanel
                                value="1"
                                className="projectSettingsTabContainer"
                            >
                                <GeneralTab
                                    project={project}
                                    projectName={projectName}
                                    handleProjectNameChange={
                                        handleProjectNameChange
                                    }
                                    projectNameError={projectNameError}
                                    environment={environment}
                                    handleEnvironmentChange={
                                        handleEnvironmentChange
                                    }
                                    resolutions={resolutions}
                                    handleResolutionsChange={
                                        handleResolutionsChange
                                    }
                                    allIssueTypes={allIssueTypes}
                                    projectIssueTypes={projectIssueTypes}
                                    projectIssueTypesError={
                                        projectIssueTypesError
                                    }
                                    handleProjectIssueTypesChange={
                                        handleProjectIssueTypesChange
                                    }
                                    loginRequired={loginRequired}
                                    handleLoginRequiredChange={
                                        handleLoginRequiredChange
                                    }
                                />
                            </TabPanel>
                            <TabPanel
                                value="2"
                                className="projectSettingsTabContainer"
                            >
                                <LoginTab
                                    url={url}
                                    setUrl={setUrl}
                                    urlError={urlError}
                                    handleUrlChange={handleUrlChange}
                                    loginFormId={loginFormId}
                                    setLoginFormId={setLoginFormId}
                                    login={login}
                                    setLogin={setLogin}
                                    loginError={loginError}
                                    handleLoginChange={handleLoginChange}
                                    isPasswordExist={isPasswordExist}
                                    setIsPasswordExist={setIsPasswordExist}
                                    isEditingPassword={isEditingPassword}
                                    newPassword={newPassword}
                                    handleNewPasswordChange={
                                        handleNewPasswordChange
                                    }
                                    handleClosePassword={handleClosePassword}
                                    handleSavePassword={handleSavePassword}
                                    handleEditPassword={handleEditPassword}
                                    password={password}
                                    handlePasswordChange={handlePasswordChange}
                                    passwordError={passwordError}
                                    elementsIdRoles={elementsIdRoles}
                                    setElementsIdRoles={setElementsIdRoles}
                                    selectedSection={selectedSection}
                                    setSelectedSection={setSelectedSection}
                                    selectedElement={selectedElement}
                                    setSelectedElement={setSelectedElement}
                                    // setRescanLoginPageLoading={setRescanLoginPageLoading}
                                    setOpenLoginRequiredModal={
                                        setOpenLoginRequiredModal
                                    }
                                />
                            </TabPanel>
                            <TabPanel
                                value="3"
                                className="projectSettingsTabContainer"
                            >
                                <TestCaseConfigTab
                                    projectId={projectId}
                                    initialEmailDomain={
                                        project?.test_case_email_domain
                                    }
                                    onSaved={(updatedProject) =>
                                        setProject(updatedProject)
                                    }
                                />
                            </TabPanel>
                            {canShareProject ? (
                                <TabPanel
                                    value="4"
                                    className="projectSettingsTabContainer"
                                >
                                    <ShareProjectTab projectId={projectId} />
                                </TabPanel>
                            ) : null}
                        </TabContext>
                    </Box>
                )}
            </Box>
            <UpdateLoginCredentialsModal
                updateCredentialsOpen={openLoginRequiredModal}
                setUpdateCredentialsOpen={setOpenLoginRequiredModal}
                isOTPSettingActiveForUpdate={true}
                setIsOTPSettingActiveForUpdate={() => {}}
                onRestartComplete={handleSaveLoginRequiredData}
                onClose={handleCloseLoginRequiredData}
                isInitialSetup={true}
            />
        </Box>
    );
};

export default ProjectSettings;
